import { useMutation } from "@blitzjs/rpc"
import { Membership, User } from "@superwall/db"
import signup from "app/auth/mutations/signup"
import { SignupFormSchema } from "app/auth/validations"
import { Form, FORM_ERROR } from "app/core/components/form/Form"
import { LabeledTextField } from "app/core/components/form/LabeledTextField"
import { LabeledTextFieldV2 } from "app/core/components/form/LabeledTextFieldV2"
import { toast } from "app/core/components/hints"
import { messageSlack } from "app/utils/slack"
import { useRouter } from "next/router"

// Declare Twitter Pixel function for TypeScript
declare global {
  interface Window {
    twq?: (command: string, event: string, params: Record<string, any>) => void
  }
}

type SignupFormProps = {
  onSuccess?: ({
    user,
  }: {
    user: Omit<User, "hashedPassword" | "updatedAt"> & { memberships: Membership[] }
  }) => void
}

export const SignupForm = (props: SignupFormProps) => {
  const [signupMutation] = useMutation(signup)
  const router = useRouter()
  return (
    <Form
      submitText="Next →"
      schema={SignupFormSchema}
      validateOnBlur={false}
      initialValues={{
        firstName: ((router.query.name as string) || "").split(" ")[0] || "",
        lastName: ((router.query.name as string) || "").split(" ")[1] || "",
        email: (router.query.email as string) || "",
        password: "",
      }}
      buttonFill={true}
      buttonPosition="end"
      buttonProps={{
        variant: "juicy",
        className: "w-auto",
      }}
      onSubmit={async (values) => {
        try {
          const { firstName, lastName, ...credentials } = values
          const user = await signupMutation({
            ...credentials,
            name: firstName + " " + lastName,
          })
          if (user.failedInvite) {
            toast.error({ content: "Unable to accept invite" })
          }

          // Fire Twitter conversion event pixel
          if (typeof window !== "undefined" && window.twq) {
            window.twq("event", "tw-ohlae-pcls1", {})
          }

          props.onSuccess?.({ user })
          messageSlack("sign_up success")
        } catch (error) {
          if (error.message === "EMAIL_EXISTS") {
            return { email: "This email is already being used" }
          } else {
            return { [FORM_ERROR]: error.toString() }
          }
        }
      }}
    >
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <LabeledTextFieldV2
            className="w-full"
            name="firstName"
            label="First Name"
            placeholder="Jared"
          />
          <LabeledTextFieldV2
            className="w-full"
            name="lastName"
            label="Last Name"
            placeholder="Dunn"
          />
        </div>
        <LabeledTextFieldV2 name="email" label="Email" placeholder="email@company.com" />
        <LabeledTextFieldV2
          name="password"
          label="Password"
          placeholder="Something secret..."
          type="password"
          submitOnEnter={false}
        />
      </div>
    </Form>
  )
}

export default SignupForm
